import { RULES } from '../combat/rules';
import { CHAPTERS, PRESET_MONSTERS } from '../data/presets';
import type { Monster, SaveData } from './model';

/**
 * Homework Tower: an endless climb. Floor N is a story monster (cycling through
 * all of them by level) rebuilt with stats for level N, so it reuses existing art.
 * Every fifth floor is a boss floor. Stats stop growing at the rule caps.
 */

export const TOWER_PREFIX = 'tower:';
const BOSS_EVERY = 5;
const BY_LEVEL = [...PRESET_MONSTERS].sort((a, b) => a.level - b.level);
/** Chapter bosses (each chapter's last stage), used on boss floors. */
const BOSSES = CHAPTERS.map((c) => PRESET_MONSTERS.find((p) => p.id === c.stages.at(-1))!);

export const isBossFloor = (floor: number): boolean => floor % BOSS_EVERY === 0;

export function towerMonsterId(floor: number): string {
  return `${TOWER_PREFIX}${floor}`;
}

/** The floor of a tower monster id, or null for anything else. */
export function towerFloorOf(id: string): number | null {
  if (!id.startsWith(TOWER_PREFIX)) return null;
  const n = Number(id.slice(TOWER_PREFIX.length));
  return Number.isInteger(n) && n >= 1 && n <= 9999 ? n : null;
}

/** The floor the player climbs next. */
export function nextTowerFloor(save: SaveData): number {
  return (save.counters.towerBest ?? 0) + 1;
}

export function towerMonster(floor: number): Monster {
  const boss = isBossFloor(floor);
  const base = boss ? BOSSES[(floor / BOSS_EVERY - 1) % BOSSES.length]! : BY_LEVEL[(floor - 1) % BY_LEVEL.length]!;
  const s = RULES.stats;
  const clamp = (v: number, r: { min: number; max: number }): number => Math.max(r.min, Math.min(r.max, Math.round(v)));
  const L = floor;
  return {
    id: towerMonsterId(floor),
    name: base.name,
    hp: clamp(30 + 4.6 * L + (boss ? 14 : 0), s.hp),
    atk: clamp(5 + 0.8 * L + (boss ? 2 : 0), s.atk),
    def: clamp(1 + 0.6 * L + (boss ? 1 : 0), s.def),
    luck: clamp(1 + 0.5 * L, s.luck),
    skill: base.skill,
    personality: base.personality,
    reward: {
      exp: Math.round((25 + 12 * L) * (boss ? 1.5 : 1)),
      coins: Math.round((10 + 3 * L) * (boss ? 1.5 : 1)),
    },
    mode: 'public',
    tier: base.tier,
    drawing: null,
    preset: base.id,
    source: 'tower',
    wins: 0,
    losses: 0,
    createdAt: 0,
  };
}
